import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, Navigation } from 'lucide-react';
import { PageLayout } from '../components/PageLayout';
import { submitContactForm } from '../lib/contactApi';
import { getContactSubmitError } from '../lib/contactErrors';
import { useCompany } from '../lib/useCompany';


export const Contact = () => {
  const { companyInfo, loading, error: companyError } = useCompany();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setIsSubmitted(false);

    try {
      await submitContactForm(formData);

      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        website: '',
      });

      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (error) {
      setSubmitError(getContactSubmitError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="py-24 text-center text-neutral-600">A carregar…</div>
      </PageLayout>
    );
  }

  if (companyError || !companyInfo) {
    return (
      <PageLayout>
        <div className="py-24 text-center text-red-700">{companyError || 'Informações de contacto indisponíveis.'}</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="relative -mt-20 lg:pt-40 pt-28  pb-16 md:pb-20 bg-[#262d37] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-3xl font-bold mb-1">
              Entre em Contacto
            </h1>
            <p className="text-base md:text-lg text-neutral-300">
              Estamos prontos para transformar o seu projeto de iluminação em realidade. Fale connosco.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="lg:py-20 py-10 bg-[#f2f1ec]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#262d37] mb-2">
                  Informações de Contacto
                </h2>
                <p className="text-[#262d37] mb-8">
                  Tem alguma questão? Estamos aqui para ajudar. Entre em contacto através dos meios abaixo.
                </p>
              </div>

              <Card className="bg-white/80">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail size={24} className="text-black" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#262d37] mb-0">Email</h3>
                      <a
                        href={`mailto:${companyInfo.email}`}
                        className="text-[#262d37] hover:text-black transition-colors block"
                      >
                        {companyInfo.email}
                      </a>
                      <a
                        href={`mailto:${companyInfo.projectsEmail}`}
                        className="text-[#262d37] hover:text-black transition-colors block"
                      >
                        {companyInfo.projectsEmail}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone size={24} className="text-black" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-black mb-0">Telefone</h3>
                      <p className="text-neutral-600">
                        <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="hover:text-black transition-colors font-medium">
                          {companyInfo.phone}
                        </a>
                        <span className="block text-xs text-neutral-500 mt-0">
                          {companyInfo.phoneCallLabel}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin size={24} className="text-black" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-black mb-0">Localização</h3>
                      <p className="text-neutral-600">{companyInfo.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/80">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-black mb-3">Horário de Atendimento</h3>
                  <div className="space-y-2 text-sm text-neutral-600">
                    <div className="flex justify-between">
                      <span>Segunda - Sexta:</span>
                      <span className="font-medium">09:00 - 18:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sábado e Domingo:</span>
                      <span className="font-medium">Fechado</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-white/50">
                <CardContent className="p-6 md:p-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
                    Envie-nos uma Mensagem
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                    <div
                      className="absolute -left-[9999px] h-0 w-0 overflow-hidden opacity-0"
                      aria-hidden="true"
                    >
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        name="website"
                        type="text"
                        value={formData.website}
                        onChange={handleChange}
                        autoComplete="off"
                        tabIndex={-1}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nome Completo *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="O seu nome"
                          required
                          maxLength={150}
                          autoComplete="name"
                          className="border-neutral-300"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="seu.email@exemplo.com"
                          required
                          maxLength={255}
                          autoComplete="email"
                          className="border-neutral-300"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Telefone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+351 XXX XXX XXX"
                          maxLength={30}
                          autoComplete="tel"
                          className="border-neutral-300"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">Assunto *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="Pedido de orçamento"
                          required
                          maxLength={200}
                          className="border-neutral-300"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Mensagem *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Descreva o seu projeto ou questão..."
                        rows={6}
                        required
                        maxLength={5000}
                        className="border-neutral-300 resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full md:w-auto bg-[#262d37] text-white hover:bg-neutral-800 group"
                    >
                      {isSubmitting ? (
                        'A Enviar...'
                      ) : (
                        <>
                          Enviar Mensagem
                          <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                        </>
                      )}
                    </Button>

                    {isSubmitted && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                        <CheckCircle className="text-green-600 shrink-0" size={24} />
                        <p className="text-green-800 font-medium">
                          Mensagem enviada com sucesso! Entraremos em contacto em breve.
                        </p>
                      </div>
                    )}

                    {submitError && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <AlertCircle className="text-red-600 shrink-0 mt-0.5" size={24} />
                          <div className="space-y-2">
                            <p className="text-red-800 font-medium">{submitError.summary}</p>
                            {submitError.details?.length > 0 && (
                              <ul className="list-disc pl-5 space-y-1 text-red-700 text-sm">
                                {submitError.details.map((detail) => (
                                  <li key={detail}>{detail}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="lg:py-20 py-10 bg-[#e8e6df]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#262d37] mb-1.5">
              Visite-nos
            </h2>
            <p className="text-base md:text-lg text-[#262d37]">
              Estamos localizados em Águeda
            </p>
          </div>
          <div className="bg-[#f5f7fa] rounded-lg h-96 flex items-center justify-center overflow-hidden">
            <iframe
              src="https://maps.google.com/maps?q=R.%20Celestino%20Neto%2017%201%C2%BAA%2C%203750-101%20%C3%81gueda&t=&z=15&ie=UTF8&iwloc=&output=embed"
              className="w-full h-full border-0"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Mapa de Águeda"
            ></iframe>
          </div>
          <div className="mt-6 flex justify-center">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(companyInfo.address)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                className="bg-[#262d37] text-white hover:bg-neutral-800"
              >
                <Navigation size={18} />
                Obter Direções
              </Button>
            </a>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};
